import { format, utcToZonedTime } from "date-fns-tz"
import PropTypes from "prop-types"
import React from "react"
import { useMutation } from "react-query"
import { ExportAction } from "src/actions/reservations"
import { useMarina } from "src/contexts/MarinaContext"

import Badge from "src/components/Badge"
import BoatPreviewLink from "src/components/BoatPreviewLink"
import CollapsibleBar from "src/components/CollapsibleBar"
import DataTableV2 from "src/components/DataTableV2"
import InlineNotice from "src/components/InlineNotice"
import InsightCard from "src/components/InsightCard"
import InsightSummary from "src/components/InsightSummary"
import Tooltip from "src/components/Tooltip"

import {
  exportReservations,
  getReservationSelectableIds,
  getReservations,
  printReservations,
} from "src/api/Reservation"

import useDatatable from "src/hooks/use_datatable"
import { useToast } from "src/hooks/use_toast"

import {
  formatCurrencyFromMinorUnits,
  inchesToFeet,
} from "src/utils/UnitConversion"
import { statusBadgeColor } from "src/utils/status_badge_color_helper"
import { capitalize, snakecaseToTitlecase } from "src/utils/string_helpers"

import { DockwaLogo } from "./DockwaLogo"
import FilterChips from "./FilterChips"
import Filters from "./Filters"

const formattedDate = (date) => {
  const zonedDate = utcToZonedTime(date, "UTC")
  return format(zonedDate, "MM/dd/yyyy")
}

const percentage = (numerator, denominator) => {
  if (!denominator) return null
  return Math.round((numerator / denominator) * 100)
}

const thousandsFormatter = (number) => {
  if (number == null) return ""
  return Number(number).toLocaleString()
}

const COLUMNS = [
  {
    key: "metaStatus",
    header: "Status",
    width: "150px",
    cardRole: "hidden",
    render: (row) =>
      row.metaStatus && (
        <Badge
          color={statusBadgeColor(snakecaseToTitlecase(row.metaStatus))}
          text={snakecaseToTitlecase(row.metaStatus)}
          variant="small"
        />
      ),
  },
  {
    key: "reservationId",
    header: "Reservation",
    width: "110px",
    sortable: true,
    cardRole: "hidden",
  },
  {
    key: "contactName",
    header: "Captain",
    sortable: true,
    cardRole: "title",
    width: "150px",
    cardOrder: 0,
    render: (row) => <div className="truncate">{row.contactName}</div>,
    cardRender: (row) => (
      <div className="space-y-1">
        <div className="flex w-full flex-wrap items-center justify-between gap-1">
          <div>{row.contactName}</div>
          {row.metaStatus && (
            <Badge
              color={statusBadgeColor(snakecaseToTitlecase(row.metaStatus))}
              text={snakecaseToTitlecase(row.metaStatus)}
              variant="small"
            />
          )}
        </div>
        {row.contactBoat && (
          <div className="flex items-center gap-2 text-sm font-normal">
            <BoatPreviewLink boat={row.contactBoat} />
            {row.contactBoat.lengthOverall && (
              <span className="text-xs text-gray-600">
                LOA: {inchesToFeet(row.contactBoat.lengthOverall)}&apos;
              </span>
            )}
          </div>
        )}
        <div className="flex items-center gap-2 text-sm font-normal text-gray-700">
          <span>
            {formattedDate(row.checkInDate)} &ndash;{" "}
            {formattedDate(row.checkOutDate)}
          </span>
          <span className="text-gray-400">&middot;</span>
          <span className="text-gray-500">#{row.reservationId}</span>
        </div>
      </div>
    ),
  },
  {
    key: "contactBoatName",
    header: "Boat",
    sortKey: "boat_name",
    width: "150px",
    sortable: true,
    cardRole: "hidden",
    render: (row) =>
      row.contactBoat ? <BoatPreviewLink boat={row.contactBoat} /> : null,
  },
  {
    key: "checkInDate",
    header: "Arrival",
    width: "110px",
    sortable: true,
    cardRole: "hidden",
    render: (row) => formattedDate(row.checkInDate),
  },
  {
    key: "checkOutDate",
    header: "Departure",
    width: "110px",
    sortable: true,
    cardRole: "hidden",
    render: (row) => formattedDate(row.checkOutDate),
  },
  {
    key: "nightsAtMarina",
    width: "75px",
    header: "Nights",
    sortable: true,
  },
  {
    key: "lengthOverall",
    header: "LOA",
    width: "75px",
    sortable: true,
    cardRole: "hidden",
    render: (row) =>
      row.contactBoat?.lengthOverall
        ? `${inchesToFeet(row.contactBoat.lengthOverall)}'`
        : null,
  },
  {
    key: "beam",
    header: "Beam",
    width: "75px",
    sortable: true,
    cardRole: "hidden",
    render: (row) =>
      row.contactBoat?.beam ? `${inchesToFeet(row.contactBoat.beam)}'` : null,
  },
  {
    key: "draw",
    header: "Draft",
    width: "75px",
    sortable: true,
    cardRole: "hidden",
    render: (row) =>
      row.contactBoat?.draw ? `${inchesToFeet(row.contactBoat.draw)}'` : null,
  },
  {
    key: "requestedSpaceType",
    width: "110px",
    header: "Space Type",
    sortable: true,
    render: (row) =>
      row.requestedSpaceType && capitalize(row.requestedSpaceType),
  },
  {
    key: "revenueItemName",
    header: "Revenue Item",
  },
  {
    key: "createdAt",
    width: "110px",
    header: "Created",
    sortable: true,
    render: (row) => formattedDate(row.createdAt),
  },
  {
    key: "confirmedAt",
    width: "110px",
    header: "Confirmed",
    sortable: true,
    render: (row) => (row.confirmedAt ? formattedDate(row.confirmedAt) : ""),
  },
  {
    key: "metaSource",
    width: "100px",
    header: "Source",
    sortable: true,
    cardRole: "hidden",
    render: (row) => {
      const boaterSource = row.source === "boater"
      return (
        <div className="w-min">
          <Tooltip text={row.metaSource} placement="left" variant="dark">
            {boaterSource ? (
              <DockwaLogo />
            ) : (
              <i className="fa-kit fa-trackpad text-2xl text-gray-500" />
            )}
          </Tooltip>
        </div>
      )
    },
  },
  {
    key: "totalMarinaPrice",
    width: "110px",
    header: "Price",
    render: (row) =>
      formatCurrencyFromMinorUnits({ amount: row.totalMarinaPrice }),
  },
]

const Reservations = ({
  canExport,
  exportPath,
  insights,
  reservationStatuses,
  spaceGroups,
}) => {
  const { marinaSlug } = useMarina()
  const showToast = useToast()

  const table = useDatatable({
    queryKey: "reservations",
    fetchFn: (params) => getReservations(marinaSlug, params),
    defaultFilters: {
      searchTerm: "",
      statuses: [],
      spaceGroups: [],
      inMarinaStartDate: null,
      inMarinaEndDate: null,
      arrivalDate: null,
      departureDate: null,
      nightsMin: null,
      nightsMax: null,
      paymentStatuses: [],
    },
    fetchAllIds: (filterParams) =>
      getReservationSelectableIds(marinaSlug, filterParams).then((r) => r.ids),
    idKey: "reservationId",
    staleTime: 30000,
  })

  const { mutate: triggerExport } = useMutation(
    (ids) => exportReservations({ marinaSlug, ids }),
    {
      onSuccess: () => {
        table.setSelection([])
        showToast("Export is being generated and will be emailed shortly.", {
          type: "success",
        })
      },
      onError: () => {
        showToast("Unable to start export. Please try again.", {
          type: "error",
        })
      },
    }
  )

  const handleBulkExport = (selectedIds) => {
    if (!selectedIds?.length) return
    triggerExport(selectedIds)
  }

  const ACTIONS = [
    ...(canExport ? [ExportAction({ onExport: handleBulkExport })] : []),
  ]

  return (
    <div className="bg-white px-2 lg:px-6">
      {insights && (
        <div className="mb-4 print:hidden">
          <CollapsibleBar
            title={
              <span className="text-blue-800">
                {insights.year} Insights <i className="fa-solid fa-stars" />
              </span>
            }
            ariaLabel={`Toggle ${insights.year} Insights panel`}
            accent="bg-gradient-to-r from-blue-500 via-blue-900 to-blue-700"
            summary={
              <InsightSummary
                items={[
                  {
                    value: `${
                      percentage(
                        insights.marketplaceReservations,
                        insights.reservations
                      ) ?? 0
                    }%`,
                    label: "Dockwa sourced",
                  },
                  {
                    value: thousandsFormatter(insights.repeatCustomers),
                    label: "repeat stays",
                  },
                  {
                    value: `${insights.avgLengthOfStay} days`,
                    label: "avg stay",
                  },
                  {
                    value: `${thousandsFormatter(
                      inchesToFeet(insights.linearInchesSold)
                    )}'`,
                    label: "linear ft sold",
                  },
                ]}
              />
            }
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <InsightCard
                icon="fa-md-directions-boat"
                title="Dockwa sourced"
                value={`${
                  percentage(
                    insights.marketplaceReservations,
                    insights.reservations
                  ) ?? 0
                }%`}
                description={`Out of ${thousandsFormatter(
                  insights.reservations
                )} total`}
              />
              <InsightCard
                icon="fa-md-group"
                title="Repeat stays"
                value={thousandsFormatter(insights.repeatCustomers)}
                description={`${
                  percentage(insights.repeatCustomers, insights.reservations) ??
                  0
                }% of confirmed`}
              />
              <InsightCard
                icon="fa-md-tag"
                title="Average length of stay"
                value={`${insights.avgLengthOfStay} days`}
                description={`Booked ${insights.avgReservationRequestDaysInAdvance} days in advance`}
              />
              <InsightCard
                icon="fa-waves"
                title="Linear feet sold"
                value={`${thousandsFormatter(
                  inchesToFeet(insights.linearInchesSold)
                )}'`}
                description={`${thousandsFormatter(
                  inchesToFeet(insights.linearInchesSoldPerMonth)
                )}' avg/month`}
              />
            </div>
          </CollapsibleBar>
        </div>
      )}

      {canExport && (
        <div className="mb-4 print:hidden">
          <InlineNotice
            variant="info"
            icon="fa-export-sheet"
            dismissible
            storagePrefix="reservations_index"
            storageKey="export_moved_to_bulk_actions_v1"
          >
            Looking for Export? Select rows, then click <strong>Export</strong>.
          </InlineNotice>
        </div>
      )}

      <div className="mb-4 print:hidden">
        <InlineNotice
          variant="info"
          icon="fa-print"
          iconStyle="solid"
          dismissible
          storagePrefix="reservations_index"
          storageKey="print_moved_to_bulk_actions_v1"
        >
          Looking for Print? Select rows, then click <strong>Print</strong>.
        </InlineNotice>
      </div>

      <div className="print:hidden">
        <Filters
          table={table}
          reservationStatuses={reservationStatuses}
          spaceGroups={spaceGroups}
          exportPath={exportPath}
        />
        <FilterChips table={table} spaceGroups={spaceGroups} />
      </div>

      {table.filters.paymentStatuses?.includes("awaiting_refund") && (
        <div className="print:hidden">
          <InlineNotice variant="info" icon="fa-cash-multiple">
            <strong>Awaiting marina refund:</strong> These are reservations that
            have been canceled by the boater and are pending a refund from the
            marina.
          </InlineNotice>
        </div>
      )}

      <div className="z-0 mt-4 overflow-auto pb-12">
        <DataTableV2
          name="reservations"
          columns={COLUMNS}
          data={table.records}
          actions={ACTIONS}
          rowConfig={{ getViewUrl: (row) => row.linkPath }}
          maxHeight="none"
          emptyState={
            <div className="flex flex-col text-center">
              <span className="mb-2 text-xl font-bold">
                No reservations to display
              </span>
              <span className="text-gray-600">
                Try adjusting your filters to find what you&apos;re looking for.
              </span>
            </div>
          }
          onPrint={(ids) => {
            const { page, ...printParams } = table.fetchParams
            return printReservations({ marinaSlug, ids, ...printParams })
          }}
          {...table.tableProps}
        />
      </div>
    </div>
  )
}

Reservations.propTypes = {
  canExport: PropTypes.bool,
  exportPath: PropTypes.string,
  insights: PropTypes.shape({
    year: PropTypes.number,
    reservations: PropTypes.number,
    marketplaceReservations: PropTypes.number,
    repeatCustomers: PropTypes.number,
    avgLengthOfStay: PropTypes.number,
    avgReservationRequestDaysInAdvance: PropTypes.number,
    linearInchesSold: PropTypes.number,
    linearInchesSoldPerMonth: PropTypes.number,
  }),
  reservationStatuses: PropTypes.array,
  spaceGroups: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    })
  ),
}

export default Reservations
